// 📄 utils/plantillaIdUtils.ts
// Generación del id de Firestore para una plantilla del Validador de
// Estructura, compartida entre CargarPlantillaModal.tsx (proyecto) y
// PlantillaGlobalFormModal.tsx (Admin, solo al crear) — antes cada flujo
// tenía su propio criterio (proyecto slugificaba a minúsculas; global
// dejaba que el admin escribiera el id a mano, sin transformación). Ahora
// ambos derivan el id del nombre legible que se escribe en el formulario,
// con un sufijo de Document Stage para poder tener varias plantillas con
// el mismo nombre en distintos stages sin pisarse (ver
// construirIdPlantilla).

// Marca diacrítica combinante (acento) tras normalize("NFD") — separa cada
// letra acentuada en [letra base] + [marca]; el charCode de esas marcas
// siempre cae en 0x0300–0x036F. Se filtran para que "Confirmación" quede
// "Confirmacion" en vez de perder la Ó entera o dejar un guión suelto feo.
const esMarcaDiacritica = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return code >= 0x0300 && code <= 0x036f;
};

/**
 * Limpia un segmento de texto para que sirva como parte de un id de
 * Firestore: normaliza tildes a su letra base, espacios → guiones, y
 * elimina cualquier otro carácter que no sea alfanumérico o guión.
 * A diferencia del slugify típico, PRESERVA mayúsculas/minúsculas tal como
 * se escribieron — el id sigue siendo legible ("Orden-de-Compra-de-Buying"
 * en vez de "orden-de-compra-de-buying").
 */
export const limpiarIdSegmento = (texto: string): string =>
  texto
    .trim()
    .normalize("NFD")
    .split("")
    .filter((char) => !esMarcaDiacritica(char))
    .join("")
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * Id compuesto: {nombreBase limpio}[-{documentStage}]. Sin stage (vacío o
 * "Sin especificar", el valor del <option> por defecto en
 * PlantillaMetadatosForm.tsx) el id queda igual que el comportamiento de
 * siempre — el sufijo solo se agrega cuando hay un stage real elegido, así
 * dos plantillas con el mismo nombre pero distinto stage no se pisan.
 */
export const construirIdPlantilla = (nombreBase: string, documentStage?: string): string => {
  const base = limpiarIdSegmento(nombreBase);
  const stage = (documentStage || "").trim();
  if (!stage || stage === "Sin especificar") return base;
  return `${base}-${limpiarIdSegmento(stage)}`;
};
